import { parseMoneyToCents } from './money'

/**
 * The courier quote - what delivery to a point costs where external couriers deliver. `FRONTEND.md` §9.786, on
 * BACKEND.md §10A.438 B, owner O139 (*"doprava sa počíta z odmeny šoféra"*).
 *
 * `GET /delivery/quote` answers the restaurant's mode with no address, and the courier's reward for the road distance
 * with one. **One reader for two screens**: the customer checkout prices a web order from it and the till prefills a
 * phone order's fee from it, and two readers of one money answer is how two screens quote two prices.
 */

export const DELIVERY_QUOTE_PATH = '/api/v1/delivery/quote'

export function quotePath(point: { lat: number; lng: number } | null): string {
  if (point === null) return DELIVERY_QUOTE_PATH
  return `${DELIVERY_QUOTE_PATH}?lat=${String(point.lat)}&lon=${String(point.lng)}`
}

export interface CourierQuote {
  /** Whether this restaurant delivers through external couriers - the only mode where any of this applies. */
  external: boolean
  /** The fee for the asked address, null when no address was asked about. */
  feeCents: number | null
  km: number | null
  /** False when the address is beyond the farthest distance. Null when no address was asked about. */
  withinRange: boolean | null
  maxKm: number | null
  minimumCents: number
}

function decimal(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

/** The server's answer, or null when it is not one - a restaurant whose quote could not be read keeps its zones. */
export function courierQuoteOf(payload: unknown): CourierQuote | null {
  if (typeof payload !== 'object' || payload === null) return null
  const row = payload as Record<string, unknown>
  if (row.mode !== 'own' && row.mode !== 'external') return null
  const fee = decimal(row.fee)
  return {
    external: row.mode === 'external',
    feeCents: fee === null ? null : parseMoneyToCents(fee),
    km: decimal(row.km),
    withinRange: typeof row.withinRange === 'boolean' ? row.withinRange : null,
    maxKm: decimal(row.maxKm),
    minimumCents: parseMoneyToCents(decimal(row.minimumOrder) ?? 0),
  }
}
